import { useCallback, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { updateCall, setActiveCallSession } from "@/redux/slices/callsSlice";
import { selectPaymentRequired, selectUser } from "@/redux/slices/authSlice";
import { dailyService } from "@/lib/services/daily.service";
import { callsAPI } from "@/lib/services/api.service";
import { getSocketService } from "@/lib/utils/socket";

export const useCallManagement = () => {
  const dispatch = useAppDispatch();
  const paymentRequired = useAppSelector(selectPaymentRequired);
  const user = useAppSelector(selectUser);
  const [isInitiating, setIsInitiating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initiateCall = useCallback(
    async (visitorId: string, callType: "video" | "voice") => {
      try {
        setIsInitiating(true);
        setError(null);

        if (paymentRequired && user?.role !== "SUPER_ADMIN") {
          throw new Error("Add a payment method under Billing before starting calls.");
        }

        const socketService = getSocketService();
        if (!socketService || !socketService.isConnected()) {
          throw new Error("Real-time connection unavailable");
        }

        socketService.initiateCall(visitorId, callType);
        return { success: true };
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to initiate call";
        setError(message);
        return { success: false, error: message };
      } finally {
        setIsInitiating(false);
      }
    },
    [dispatch, paymentRequired, user?.role]
  );

  const joinVideoCall = useCallback(async (roomUrl: string, userName: string) => {
    try {
      setError(null);

      // Join Daily.co call
      await dailyService.joinCall({
        roomUrl,
        userName,
        startVideoOff: false,
        startAudioOff: false,
      });

      // Set up event handlers
      dailyService.on("left-meeting", () => {
        console.log("[Call] Left meeting");
        dailyService.leaveCall();
      });

      dailyService.on("participant-joined", (event: any) => {
        console.log("[Call] Participant joined:", event.participant);
      });

      dailyService.on("participant-left", (event: any) => {
        console.log("[Call] Participant left:", event.participant);
      });

      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to join video call";
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const endCall = useCallback(
    async (callId: string, callInfo?: { type: string; duration: number }) => {
      try {
        setError(null);

        // Leave Daily.co call if in one
        if (dailyService.isInCall()) {
          await dailyService.leaveCall();
        }

        // Call backend API to complete call
        const socketService = getSocketService();
        if (socketService) {
          socketService.endCall(callId);
        }

        dispatch(setActiveCallSession({ call: null }));

        // Return call info for post-call survey
        return {
          success: true,
          callId,
          callInfo,
        };
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to end call";
        setError(message);
        return { success: false, error: message };
      }
    },
    [dispatch]
  );

  const markMeetingBooked = useCallback(
    async (callId: string) => {
      try {
        setError(null);

        const { call } = await callsAPI.markMeetingBooked(callId);

        // Update Redux state
        dispatch(updateCall(call));

        return { success: true, call };
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to mark meeting as booked";
        setError(message);
        return { success: false, error: message };
      }
    },
    [dispatch]
  );

  const toggleCamera = useCallback(async () => {
    try {
      await dailyService.toggleCamera();
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to toggle camera";
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const toggleMicrophone = useCallback(async () => {
    try {
      await dailyService.toggleMicrophone();
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to toggle microphone";
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const startScreenShare = useCallback(async () => {
    try {
      await dailyService.startScreenShare();
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to start screen share";
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  const stopScreenShare = useCallback(async () => {
    try {
      await dailyService.stopScreenShare();
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to stop screen share";
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  return {
    initiateCall,
    joinVideoCall,
    endCall,
    markMeetingBooked,
    toggleCamera,
    toggleMicrophone,
    startScreenShare,
    stopScreenShare,
    isInitiating,
    error,
  };
};
